/**
 * Canvas geometry shared by the node renderer, the edges and the editor —
 * the concept's `flow/layout.js`, typed.
 *
 * Widths are fixed per node type rather than measured because placement has to
 * happen BEFORE a new node is in the DOM: the editor finds a free spot for a
 * node it has not rendered yet, and a width read after the fact would let two
 * nodes overlap for a frame and then jump. Heights ARE measured once rendered
 * (a WhatsApp bubble grows with its template); `EST_H` is only the guess the
 * placer uses for a node that has never been drawn.
 */
import type { FlowNodeType } from '@wend/shared';

export const CARD_W: Readonly<Partial<Record<FlowNodeType, number>>> = {
  trigger: 250,
  message: 290,
  update: 230,
  goal: 220,
  exit: 220,
  comment: 220,
};
export const ROUND_W = 160;

export const EST_H: Readonly<Record<FlowNodeType, number>> = {
  trigger: 150,
  message: 300,
  update: 110,
  goal: 110,
  exit: 110,
  comment: 110,
  split: 220,
  wait: 140,
  condition: 200,
};

/** Split, wait and condition are the round gradient logic nodes with a label under them. */
export function isRound(type: FlowNodeType): boolean {
  return type === 'split' || type === 'wait' || type === 'condition';
}

export function nodeWidth(type: FlowNodeType): number {
  return isRound(type) ? ROUND_W : (CARD_W[type] ?? 240);
}

/** Where the in-anchor sits below a node's top edge — the y an incoming edge aims at. */
export function inOffset(type: FlowNodeType): number {
  return isRound(type) ? 27 : 24;
}

export interface Point {
  x: number;
  y: number;
}

/** A cubic connector leaving a port horizontally and arriving horizontally. */
export function curve(a: Point, b: Point): { d: string; mid: Point } {
  const x1 = a.x + 6;
  const x2 = b.x - 3;
  const dx = Math.max(46, Math.abs(x2 - x1) * 0.5);
  const c1 = { x: x1 + dx, y: a.y };
  const c2 = { x: x2 - dx, y: b.y };
  return {
    d: `M ${x1} ${a.y} C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${x2} ${b.y}`,
    mid: { x: (x1 + 3 * c1.x + 3 * c2.x + x2) / 8, y: (a.y + 3 * c1.y + 3 * c2.y + b.y) / 8 },
  };
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Room a suggested node needs above it for its "Suggested ✓ ✕" bar. */
export const SUGGEST_BAR_H = 46;

/**
 * The concept's placer: walk right in 90px steps and try a fan of vertical
 * offsets around the port's height until the node's rectangle (plus a gutter)
 * touches nothing. Falls back to the port's own height when the canvas is
 * crowded — an overlap the person can drag apart beats a node 2,000px away.
 */
export function findSpot(
  occupied: Rect[],
  type: FlowNodeType,
  x0: number,
  yIn: number,
  suggested: boolean,
): Point {
  const w = nodeWidth(type);
  const top = suggested ? SUGGEST_BAR_H : 0;
  const h = EST_H[type] + top;
  const hits = (r: Rect) => occupied.some((o) => r.x < o.x + o.w + 28 && r.x + r.w + 28 > o.x && r.y < o.y + o.h + 24 && r.y + r.h + 24 > o.y);
  for (let dx = 0; dx < 1200; dx += 90) {
    for (const dy of [0, 40, -40, 80, -80, 130, -130, 190, -190, 260, 330, 400, 480]) {
      const y = Math.max(16 + top, yIn - inOffset(type) + dy);
      const r = { x: x0 + dx, y: y - top, w, h };
      if (!hits(r)) {
        occupied.push(r);
        return { x: x0 + dx, y };
      }
    }
  }
  return { x: x0, y: Math.max(16 + top, yIn - inOffset(type)) };
}
